import { FC } from 'react'
import { Meeting, MeetingDashboardFragment, MeetingStatus } from '../../../types'
import { useTranslation } from 'react-i18next'
import { Colors, IconNames } from '../../../components/util/types'
import { useHistory } from 'react-router'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import PerButton from '../../../components/custom_components/Button'

type ManagerMeetingActionButtonProps = {
  meeting: MeetingDashboardFragment
  type: 'rectangular' | 'circle'
  disabledTooltip?: string
}

const ManagerMeetingActionButton: FC<ManagerMeetingActionButtonProps> = ({ meeting, type, disabledTooltip }) => {
  const [t] = useTranslation()
  const history = useHistory()

  const isConfirmed = meeting.status === MeetingStatus.Confirmed
  const isCanceled = meeting.status === MeetingStatus.Canceled

  const actionPrepareManager = !isCanceled && !meeting.managerPrepared

  const actionStartManager = !isCanceled && meeting.managerPrepared

  const isMeetingCallable = isConfirmed || isCanceled || !!disabledTooltip

  const meetingActionIconName = (): IconNames => {
    if (actionStartManager) {
      return 'chat_fill'
    } else if (actionPrepareManager) {
      return 'edit_fill'
    } else if (isCanceled) {
      return 'close_stroke'
    }
    return 'close_stroke' // fallback
  }

  const meetingE2eActionType = (): string => {
    if (actionStartManager) {
      return 'start'
    } else if (actionPrepareManager) {
      return 'prepare'
    } else if (isCanceled) {
      return 'cancel'
    }
    return 'cancel' // fallback
  }

  const meetingActionIconColor = (): Colors => {
    if (actionStartManager) {
      return 'success'
    } else if (actionPrepareManager) {
      return 'warning'
    }
    return 'warning' // fallback
  }

  const meetingActionTooltip = (): string => {
    if (!!disabledTooltip) {
      return disabledTooltip
    }
    if (actionStartManager) {
      return t('meetingButtonActions.start', { meetingType: t(`meetingTypes.${meeting.type}`) })
    } else if (actionPrepareManager) {
      return t('meetingButtonActions.prep', { meetingType: t(`meetingTypes.${meeting.type}`) })
    }

    return '' // fallback
  }

  const meetingActionOnClickHandler = (meeting: Pick<Meeting, 'id'>) => {
    if (actionStartManager) {
      return history.push('/meetings/' + meeting.id)
    } else if (actionPrepareManager) {
      return history.push('/managerPrepareMeeting/' + meeting.id)
    }
  }

  const meetingActionButtonText = () => {
    if (isCanceled) {
      return t('dashboard.employee.meetings.canceled')
    } else if (isConfirmed) {
      return t('dashboard.employee.meetings.confirmed')
    } else if (actionStartManager) {
      return t('meetingButtonActions.start', { meetingType: t(`meetingTypes.${meeting.type}`) })
    } else if (actionPrepareManager) {
      return t('meetingButtonActions.prep', { meetingType: t(`meetingTypes.${meeting.type}`) })
    }
    return '' // fallback
  }

  return (
    <>
      {type === 'rectangular' && (
        <PerButton
          onClick={() => meetingActionOnClickHandler(meeting)}
          color={meetingActionIconColor()}
          variant="pastel"
          text={meetingActionButtonText()}
          before={meetingActionIconName()}
          size="small"
          width="fullWidth"
          disabled={isMeetingCallable}
          data-e2e-manager-meeting-action-button={meetingE2eActionType()}
        />
      )}
      {type === 'circle' && (
        <PerButtonCircle
          onClick={() => meetingActionOnClickHandler(meeting)}
          color={meetingActionIconColor()}
          icon={meetingActionIconName()}
          variant="pastel"
          tooltip={meetingActionTooltip()}
          disabled={isMeetingCallable}
          data-e2e-manager-meeting-action-button={meetingE2eActionType()}
        />
      )}
    </>
  )
}

export default ManagerMeetingActionButton
